/**
 * Theme Settings Page — planTEK admin portal branding configuration.
 * Route: /theme-settings
 * Gated: FeatureGate feature="theming" (in App.tsx)
 *
 * Fields: company name, tagline, primary/secondary/accent colors,
 *         logo upload, favicon URL.
 * Live preview card updates as admin changes values.
 * Save persists to backend; Reset reverts to DEFAULT_THEME.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Palette, Save, RotateCcw, Upload, X, AlertCircle, Image as ImageIcon, Globe } from 'lucide-react';
import { HexColorPicker } from 'react-colorful';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { usePortalTheme, useUpdatePortalTheme } from '@/hooks';
import { api } from '@/lib/api';
import { DEFAULT_THEME, contrastForeground } from '@/lib/theme';
import type { TenantTheme } from '@/lib/theme';

const FONT_OPTIONS = [
  { value: 'system-ui', label: 'System UI (default)' },
  { value: 'Inter', label: 'Inter' },
  { value: 'Roboto', label: 'Roboto' },
];

interface ThemeFormState {
  primary: string;
  secondary: string;
  accent: string;
  font_family: string;
  logo_url: string;
  favicon_url: string;
  company_name: string;
  tagline: string;
}

const FORM_DEFAULTS: ThemeFormState = {
  primary: DEFAULT_THEME.colors.primary,
  secondary: DEFAULT_THEME.colors.secondary ?? '',
  accent: DEFAULT_THEME.colors.accent ?? '',
  font_family: DEFAULT_THEME.typography.font_family,
  logo_url: '',
  favicon_url: '',
  company_name: '',
  tagline: '',
};

function themeToForm(t: TenantTheme): ThemeFormState {
  return {
    primary: t.colors?.primary ?? DEFAULT_THEME.colors.primary,
    secondary: t.colors?.secondary ?? DEFAULT_THEME.colors.secondary ?? '',
    accent: t.colors?.accent ?? DEFAULT_THEME.colors.accent ?? '',
    font_family: t.typography?.font_family ?? 'system-ui',
    logo_url: t.branding?.logo_url ?? '',
    favicon_url: t.branding?.favicon_url ?? '',
    company_name: t.branding?.company_name ?? '',
    tagline: (t.branding as Record<string, unknown>)?.tagline as string ?? '',
  };
}

// ─── Color field with picker ─────────────────────────────────────────────────

interface ColorFieldProps {
  label: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  testId?: string;
}

function ColorField({ label, required, value, onChange, placeholder, testId }: ColorFieldProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={wrapRef}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          data-testid={testId ? `${testId}-swatch` : undefined}
          className="w-12 h-12 rounded-lg border-2 border-gray-300 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500"
          style={{ backgroundColor: value || placeholder }}
          title="Click to open colour picker"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          pattern="^#[0-9A-Fa-f]{6}$"
          maxLength={7}
          data-testid={testId ? `${testId}-input` : undefined}
          className="w-32 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder={placeholder}
        />
      </div>
      {open && (
        <div className="mt-3 p-4 bg-white border border-gray-300 rounded-lg shadow-xl inline-block z-10 relative">
          <HexColorPicker color={value || placeholder} onChange={onChange} />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3 w-full"
            onClick={() => setOpen(false)}
          >
            Done
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Live preview card ────────────────────────────────────────────────────────

interface PreviewCardProps {
  form: ThemeFormState;
}

function PreviewCard({ form }: PreviewCardProps) {
  const fg = contrastForeground(form.primary);
  const secondaryBg = form.secondary || DEFAULT_THEME.colors.secondary!;
  const accentBg = form.accent || DEFAULT_THEME.colors.accent!;

  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden shadow-sm">
      {/* Header bar */}
      <div
        className="flex items-center gap-3 px-4 py-3"
        style={{ backgroundColor: form.primary }}
      >
        {form.logo_url ? (
          <img
            src={form.logo_url}
            alt="Portal logo"
            className="h-6 w-auto object-contain"
          />
        ) : (
          <Globe className="h-5 w-5 flex-shrink-0" style={{ color: fg }} />
        )}
        <div style={{ color: fg }}>
          <p className="text-sm font-semibold leading-tight">
            {form.company_name || 'Company Name'}
          </p>
          {form.tagline && (
            <p className="text-xs opacity-80 leading-tight">{form.tagline}</p>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="bg-white p-4 space-y-3">
        {/* Colour swatches */}
        <div className="flex items-center gap-3">
          {[
            { colour: form.primary, label: 'Primary' },
            { colour: secondaryBg, label: 'Secondary' },
            { colour: accentBg, label: 'Accent' },
          ].map(({ colour, label }) => (
            <div key={label} className="text-center">
              <div
                className="h-8 w-8 rounded-md border border-gray-200 mx-auto"
                style={{ backgroundColor: colour }}
              />
              <p className="text-xs text-gray-500 mt-1">{label}</p>
            </div>
          ))}
        </div>

        {/* Sample button */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="px-4 py-2 rounded-md text-sm font-medium"
            style={{
              backgroundColor: form.primary,
              color: fg,
            }}
          >
            Primary button
          </button>
          <button
            type="button"
            className="px-4 py-2 rounded-md text-sm font-medium border"
            style={{
              borderColor: form.primary,
              color: form.primary,
              backgroundColor: 'transparent',
            }}
          >
            Outline
          </button>
        </div>

        {/* Sample text */}
        <p className="text-xs text-gray-500">
          Body text — shows how readable content looks against the default background.
        </p>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function ThemeSettingsPage() {
  const { data: portalThemeData, isLoading } = usePortalTheme();
  const updatePortalTheme = useUpdatePortalTheme();

  const [form, setForm] = useState<ThemeFormState>(FORM_DEFAULTS);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Initialise form from fetched data
  useEffect(() => {
    if (portalThemeData) {
      setForm(themeToForm(portalThemeData as TenantTheme));
    }
  }, [portalThemeData]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: Partial<TenantTheme> & { branding: Record<string, unknown> } = {
      colors: {
        primary: form.primary,
        ...(form.secondary && { secondary: form.secondary }),
        ...(form.accent && { accent: form.accent }),
      },
      typography: {
        font_family: form.font_family as TenantTheme['typography']['font_family'],
      },
      branding: {
        ...(form.logo_url && { logo_url: form.logo_url }),
        ...(form.favicon_url && { favicon_url: form.favicon_url }),
        ...(form.company_name && { company_name: form.company_name }),
        ...(form.tagline && { tagline: form.tagline }),
      },
    };
    await updatePortalTheme.mutateAsync(payload as Record<string, unknown>);
  };

  const handleReset = () => {
    setForm(FORM_DEFAULTS);
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoError(null);
    if (file.size > 2 * 1024 * 1024) {
      setLogoError(`File too large: ${(file.size / 1024 / 1024).toFixed(1)}MB (max 2MB)`);
      return;
    }
    setLogoUploading(true);
    try {
      const result = await api.settings.uploadThemeLogo(file);
      setForm(prev => ({ ...prev, logo_url: result.url }));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Logo upload failed';
      setLogoError(message);
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = () => {
    setForm(prev => ({ ...prev, logo_url: '' }));
  };

  if (isLoading) {
    return (
      <DashboardLayout title="Theme Settings">
        <div className="p-6 space-y-6 max-w-4xl">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Theme Settings">
      <div className="p-6 max-w-4xl">
        {/* Page header */}
        <div className="flex items-center gap-3 mb-8">
          <Palette className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-semibold">Portal Theme</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Customise the look and feel of your client-facing portal.
            </p>
          </div>
        </div>

        <form onSubmit={handleSave}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* ── Left: form controls ── */}
            <div className="space-y-6">
              {/* Company name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  value={form.company_name}
                  onChange={(e) => setForm(prev => ({ ...prev, company_name: e.target.value }))}
                  maxLength={200}
                  data-testid="company-name-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Your company name"
                />
                <p className="text-xs text-gray-500 mt-1">Shown in the portal header.</p>
              </div>

              {/* Tagline */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tagline <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={form.tagline}
                  onChange={(e) => setForm(prev => ({ ...prev, tagline: e.target.value }))}
                  maxLength={160}
                  data-testid="tagline-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Your trusted construction partner"
                />
                <p className="text-xs text-gray-500 mt-1">Short descriptor shown below the company name.</p>
              </div>

              {/* Colours */}
              <ColorField
                label="Primary Colour"
                required
                value={form.primary}
                onChange={(v) => setForm(prev => ({ ...prev, primary: v }))}
                placeholder="#2563eb"
                testId="primary-color"
              />

              <ColorField
                label="Secondary Colour"
                value={form.secondary}
                onChange={(v) => setForm(prev => ({ ...prev, secondary: v }))}
                placeholder="#7c3aed"
                testId="secondary-color"
              />

              <ColorField
                label="Accent Colour"
                value={form.accent}
                onChange={(v) => setForm(prev => ({ ...prev, accent: v }))}
                placeholder="#06b6d4"
                testId="accent-color"
              />

              {/* Font family */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Font Family
                </label>
                <select
                  value={form.font_family}
                  onChange={(e) => setForm(prev => ({ ...prev, font_family: e.target.value }))}
                  data-testid="font-family-select"
                  className="w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {FONT_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Logo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Portal Logo
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  SVG, PNG, or JPEG — max 2 MB. Delete to revert to default BuildGravity branding.
                </p>

                {form.logo_url ? (
                  /* Logo preview + controls */
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <ImageIcon className="w-4 h-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-700">Current Logo</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <label
                          htmlFor="portal-logo-upload"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md text-xs cursor-pointer hover:bg-gray-100"
                        >
                          <Upload className="w-3.5 h-3.5" />
                          Replace
                        </label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          data-testid="remove-logo-button"
                          onClick={handleRemoveLogo}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                        >
                          <X className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                    <div className="bg-white rounded border border-gray-200 flex items-center justify-center min-h-[80px] p-3">
                      <img
                        src={form.logo_url}
                        alt="Portal logo preview"
                        data-testid="logo-preview"
                        className="max-w-full max-h-24 object-contain"
                      />
                    </div>
                  </div>
                ) : (
                  /* Upload area */
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                    <label
                      htmlFor="portal-logo-upload"
                      className="cursor-pointer flex flex-col items-center gap-2"
                    >
                      <Upload className="w-8 h-8 text-gray-400" />
                      <span className="text-sm font-medium text-gray-700">
                        {logoUploading ? 'Uploading…' : 'Click to upload logo'}
                      </span>
                      <span className="text-xs text-gray-500">or drag and drop</span>
                    </label>
                  </div>
                )}

                <input
                  ref={logoInputRef}
                  id="portal-logo-upload"
                  type="file"
                  accept=".svg,.png,.jpg,.jpeg,image/svg+xml,image/png,image/jpeg"
                  onChange={handleLogoUpload}
                  className="hidden"
                  data-testid="logo-upload-input"
                />

                {logoError && (
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-700">{logoError}</p>
                  </div>
                )}
              </div>

              {/* Favicon URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Favicon URL <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  HTTPS link to a .ico, .png, or .svg file — appears in browser tabs.
                </p>
                <input
                  type="url"
                  value={form.favicon_url}
                  onChange={(e) => setForm(prev => ({ ...prev, favicon_url: e.target.value }))}
                  pattern="https://.*\.(ico|png|svg)(\?.*)?"
                  data-testid="favicon-url-input"
                  className="w-full max-w-sm px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://example.com/favicon.png"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4 border-t">
                <Button
                  type="submit"
                  disabled={updatePortalTheme.isPending || !form.primary}
                  data-testid="save-theme-button"
                  className="inline-flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {updatePortalTheme.isPending ? 'Saving…' : 'Save Changes'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                  data-testid="reset-theme-button"
                  className="inline-flex items-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset to Defaults
                </Button>
              </div>
            </div>

            {/* ── Right: live preview ── */}
            <div className="lg:sticky lg:top-6 lg:self-start space-y-3">
              <h2 className="text-sm font-medium text-gray-700">Live Preview</h2>
              <p className="text-xs text-muted-foreground">
                Updates as you change settings above.
              </p>
              <PreviewCard form={form} />
            </div>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
